import get from "lodash/get";
import { getKeyedCookieValue } from "asos-site-chrome-store-presentation";
import { buildAppliedRefinement } from "./emitterEvents";
import { TRACKING_CATEGORY_PAGE_TYPE } from "./constants";
import { getLocation } from "../state/modules/router/selectors";
import { getViewportCategory } from "../state/modules/layout/selectors";
import {
  getFacets,
  getPaginationData,
  getItemCount,
  getProducts,
  getRecommendationsAnalytics,
  getSearchMetadata,
  getSearchPassesName
} from "../state/modules/search/selectors";
import { getError } from "../state/modules/error";
import { getPageName } from "../state/modules/page/selectors";
import {
  getLocale,
  getPlatform,
  getCountry,
  getRowLength,
  getCurrencyCode
} from "../state/modules/config/selectors";
import { SEARCH_PAGE, CATEGORY_PAGE } from "../state/modules/page/constants";
import {
  getCategoryId,
  getCategoryGender,
  getCategoryTitle,
  getCategoryBreadcrumbs
} from "../state/modules/category";
import { formatCategoryBreadcrumbs } from "../helpers/analytics";
import { isFacetPriceRange } from "../state/modules/search/priceRangeSelectors";
import { options } from "../components/Sort/queryParamsToOptions";
import getTranslations from "../../i18n";

const asosBasketCookieName = "asosbasket";

export const getClientPageLoadData = state => {
  const customerIdFromCookie = getKeyedCookieValue("asos", "customerid");
  const { storeCode, defaultLanguage: language } = getCountry(state);
  const paginationData = getPaginationData(state);
  const {
    personalisationStatus,
    numberOfItems: numberOfRecommendedItems,
    items: recommendedProductsList
  } = getRecommendationsAnalytics(state);
  const products = getProducts(state);

  return {
    customerID: customerIdFromCookie ? customerIdFromCookie : -1,
    platform: getPlatform(state),
    store: storeCode.toLowerCase(),
    language,
    currency: getCurrencyCode(state),
    viewportCategory: getViewportCategory(state),
    returnedProductList: products.map(product => product.id),
    lastPage: !paginationData.meta.canLoadMore,
    pageGridSize: getRowLength(state),
    pageNumber: paginationData.page.initial,
    pageSize: products.length,
    totalStyles: getItemCount(state),
    personalisationStatus,
    numberOfRecommendedItems,
    recommendedProductsList,
    totalBagItems: getKeyedCookieValue(asosBasketCookieName, "basketitemcount"),
    totalBagValueLocalCurrency: getKeyedCookieValue(
      asosBasketCookieName,
      "basketitemtotalretailprice"
    )
  };
};

const getSortAnalyticsObject = async state => {
  const currentSort = getLocation(state).query.sort;

  const sortOptions = options(getPageName(state));

  const selectedOption = sortOptions.find(
    option => option.value === currentSort
  );

  const translations = await getTranslations(getLocale(state));

  const optionDisplayText = get(translations, selectedOption.label);

  return {
    sortType: currentSort,
    sortName: optionDisplayText
  };
};

export const getAppliedRefinementsData = async state => {
  const facetsWithSelectedValues = getFacets(state).filter(
    facet => facet.hasSelectedValues && !isFacetPriceRange(facet)
  );

  return {
    refinements: {
      context: "refinements:applied",
      listAppliedRefinements: facetsWithSelectedValues.map(facet =>
        buildAppliedRefinement(
          state,
          facet,
          facet.facetValues.filter(facetValue => facetValue.isSelected)
        )
      ),
      sort: await getSortAnalyticsObject(state)
    }
  };
};

export const getPageSpecificAnalyticsData = state => {
  const location = getLocation(state);

  switch (getPageName(state)) {
    case SEARCH_PAGE:
      return {
        floor: "search",
        pageType: "search",
        searchTerm:
          typeof location.query.q === "string"
            ? location.query.q.toLowerCase()
            : "",
        correctedSearchTerm: getSearchMetadata(state).alternateSearchTerms,
        searchType: getSearchType(location),
        searchPasses: getSearchPassesName(state),
        failedSearchType: getFailedSearchType(state),
        breadcrumbs: "search%$all"
      };
    case CATEGORY_PAGE:
      return {
        categoryID: getCategoryId(state),
        floor: getCategoryGender(state),
        pageName: getCategoryTitle(state),
        pageType: TRACKING_CATEGORY_PAGE_TYPE,
        breadcrumbs: formatCategoryBreadcrumbs(getCategoryBreadcrumbs(state))
      };
    default:
      return {};
  }
};

const getSearchType = location => {
  switch ("true") {
    case location.query.RecentSearchesUsed:
      return "recent";
    case location.query.SearchSuggestUsed:
      return "suggested";
    default:
      return "free text";
  }
};

const getFailedSearchType = state => {
  if (getError(state)) {
    return "failed by error";
  } else if (getItemCount(state) === 0) {
    return "zero results";
  }

  return "";
};
